/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { toInteger } from 'lodash';
import Typography from 'newComponents/Typography';
import { useSelector, useDispatch } from 'react-redux';
import { useSize } from 'ahooks';
import { getUserBookmarks } from '../../actions/bookmarks/bookmarks.actions';
import { selectVCardBookmarksInfoSectionState } from '../../selectors/vcard.selector';
import Skeleton, { SkeletonTheme } from '../Skeleton';
import Bookmark from '../EpisodesSection/Bookmark';
import './InfoSection.scss';
import { renderMaxLengthText } from '../../../../utils/renderMaxLengthText';
import { getTranslationEmptyOrNullKey } from 'utils/getTranslation';
import { useVerifyIsMobile } from 'hooks/useVerifyIsMobile';

const InfoSection = ({ data, className, isLoading, id }) => {
  const {
    title,
    genres,
    episodeTitle,
    episode,
    originalTitle,
    rate,
    publishDate,
    isSerie,
    duration,
    shortDescription = '',
    largeDescription,
  } = data;

  const dispatch = useDispatch();
  const userHasSession = useSelector(state => state.user.userhash);
  const { groups, isBookmarkLoading } = useSelector(
    selectVCardBookmarksInfoSectionState,
  );
  const translations = useSelector(state => state.translations);
  const [shortDesc, setShortDesc] = useState(
    renderMaxLengthText(shortDescription, 240),
  );
  const [IsExpandedDescription, setIsExpandedDescription] = useState(false);
  const [hasClampDescription, setHasClampDescription] = useState(false);
  const clampRef = useRef(null);

  const moreKey = getTranslationEmptyOrNullKey(
    'Vcard_Metadata_TextoMasInfo',
    translations,
  );
  const lessKey = getTranslationEmptyOrNullKey(
    'Vcard_Metadata_TextoMenosInfo',
    translations,
  );

  useEffect(() => {
    if (!userHasSession || !id || isBookmarkLoading) return;
    dispatch(getUserBookmarks(id, 'infoSectionType'));
  }, []);

  const genresString = genres
    ? genres
        .map(g => g.desc)
        .slice(0, 2)
        .join(', ')
    : '';
  const bookmark = groups.find(b => b.id === id);
  const time = duration && duration.split(':');

  const getRate = ratingId => {
    return translations[`${ratingId}_rating_description_label`];
  };

  const showLargeDescription = () => {
    setIsExpandedDescription(!IsExpandedDescription);
  };

  function getVisibleClampedText(parent, maxLines) {
    const child = parent.firstElementChild;
    if (!child) return '';
  
    let textNode = child.firstChild;
    while (textNode && textNode.nodeType !== Node.TEXT_NODE) {
      textNode = textNode.firstChild;
    }
    if (!textNode) return '';
  
    const fullText = textNode.textContent || '';
    const range = document.createRange();
    const style = window.getComputedStyle(child);
    const lineHeight = parseFloat(style.lineHeight);
    let result = '';
  
    for (let i = 1; i <= fullText.length; i++) {
      range.setStart(textNode, 0);
      range.setEnd(textNode, i);
      const rects = range.getClientRects();
  
      const lineCount = new Set([...rects].map(r => Math.round(r.top))).size;
  
      if (lineCount > maxLines) break;
      result = fullText.slice(0, i);
    }
  
    return result.trim();
  }

  const updateVisibleText = () => {
    setShortDesc(renderMaxLengthText(shortDesc, 240));
    if (!clampRef.current) return;
    const element = clampRef.current;
    const isClamped = element.scrollHeight > element.clientHeight;

    if (isClamped) {
      let text = getVisibleClampedText(clampRef.current, 3);

      if (clampRef.current?.innerText.length >= text.length) {
        text =
          lessKey === 'Vcard_Metadata_TextoMenosInfo'
            ? text.slice(0, -29)
            : text.slice(0, -7);
            
        setShortDesc(`${text}...`);
        setHasClampDescription(true);
      }
    } else if (shortDesc?.length >= 240 && shortDesc.endsWith('...')) {
      return setHasClampDescription(true);
    }
  };

  useEffect(
    () => {
      updateVisibleText();
      window.addEventListener('resize', updateVisibleText);
      return () => window.removeEventListener('resize', updateVisibleText);
    },
    [clampRef.current],
  );

  return (
    <div className={`InfoSection ${className}`}>
      <SkeletonTheme color="#202020" highlightColor="#444">
        <div className="InfoSection__info">
          <Typography component="h1" className="InfoSection__info__title">
            {!isLoading ? title : <Skeleton />}
          </Typography>
          <Typography component="p" className="InfoSection__info__data">
            {!isLoading ? (
              <>
                <span className="InfoSection__info__data__original-title">
                  {originalTitle}
                </span>
                <span className="InfoSection__info__data__original-title">
                  |
                </span>
                <span>{genresString}</span>
                <span>{publishDate}</span>
                <span className="InfoSection__info__data__rate">
                  {getRate(rate?.id)}
                </span>
                <span className="InfoSection__info__data__duration">
                  {time?.[0] && toInteger(time?.[0]) > 0
                    ? `${time?.[0]} h `
                    : ''}
                  {time?.[1] ? `${time?.[1]} min` : ''}
                </span>
              </>
            ) : (
              <Skeleton width={200} />
            )}
          </Typography>
          {isSerie && (
            <Typography component="h5" className="InfoSection__info__sub-title">
              {!isLoading ? (
                `Temporada ${episode.season} | Episodio ${
                  episode.number
                } : ${episodeTitle}`
              ) : (
                <Skeleton />
              )}
            </Typography>
          )}

          <Typography
            component="p"
            name="description-0"
            className="InfoSection__info__description"
            id="contentDescription"
          >
            {!isLoading ? (
              <>
                <>
                  {IsExpandedDescription ? (
                    <span>
                      {largeDescription}{' '}
                      <span onClick={showLargeDescription} className="mas">
                        {lessKey}
                      </span>
                    </span>
                  ) : (
                    <>
                      {' '}
                      <span
                        className={'InfoSection__info__description__clamp'}
                        ref={clampRef}
                      >
                        <span>{shortDesc} </span>
                        {hasClampDescription && (
                          <span onClick={showLargeDescription} className="mas">
                            {moreKey}
                          </span>
                        )}
                      </span>
                    </>
                  )}
                </>
              </>
            ) : (
              <Skeleton count={3} />
            )}
          </Typography>
          {userHasSession && bookmark ? (
            <div className="InfoSection__info__bookmark">
              <Bookmark progress={bookmark} />
            </div>
          ) : null}
        </div>
      </SkeletonTheme>
    </div>
  );
};

InfoSection.propTypes = {
  data: PropTypes.shape({
    title: PropTypes.string,
    episode: PropTypes.shape({
      season: PropTypes.string,
      number: PropTypes.string,
    }),
    duration: PropTypes.string,
    episodeTitle: PropTypes.string,
    descriptionExtended: PropTypes.string,
    genres: PropTypes.arrayOf(PropTypes.shape({})),
    originalTitle: PropTypes.string,
    isSerie: PropTypes.bool,
    publishDate: PropTypes.string,
    ranking: PropTypes.shape({}),
    rate: PropTypes.shape({
      code: PropTypes.string,
      desc: PropTypes.string,
      id: PropTypes.string,
    }),
  }),
  className: PropTypes.string,
  isLoading: PropTypes.bool,
  id: PropTypes.string,
};

InfoSection.defaultProps = {
  data: {},
  className: '',
  isLoading: false,
  id: null,
};

export default InfoSection;
